import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Input, Row, Select, Space, Table, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { listCustomers } from '../services/customerIdentityApi';
import { makeEligibilityRuleSetsApi } from '../services/eligibilityRuleSetsApi';
import EligibilityRuleSetEditor from '../components/AhriDashboard/EligibilityRuleSetEditor';

// Legacy flat-rule panels (customer_unit_eligibility_rules, migration 166)
// stay strictly indoor/outdoor -- a single flat rule can't span multiple
// slots the way a named rule set's unit_types array can.
const UNIT_TYPES = [
  { key: 'indoor', label: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor' },
];

// Named rule sets (migration 176) can apply to any subset of these three.
const RULE_SET_SLOTS = [
  { key: 'indoor', label: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor' },
  { key: 'furnace', label: 'Furnace' },
];

const emptyDraft = () => ({ manufacturer_name: '', brand_name: '', category_taxonomy_path: '' });

const UnitTypePanel = ({ unitType, label, rules, onAdd, onDelete, saving }) => {
  const [draft, setDraft] = useState(emptyDraft());

  const hasAnyDimension = Boolean(
    draft.manufacturer_name.trim() || draft.brand_name.trim() || draft.category_taxonomy_path.trim()
  );

  return (
    <Card size="small" title={`${label} unit rules`} style={{ marginBottom: 16 }}>
      {rules.length === 0 ? (
        <Empty
          description="No rules configured -- this customer is fully unrestricted for this unit type (the default)."
          style={{ margin: '12px 0' }}
        />
      ) : (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={rules}
          columns={[
            { title: 'Manufacturer', dataIndex: 'manufacturer_name', render: (v) => v || <Typography.Text type="secondary">any</Typography.Text> },
            { title: 'Brand', dataIndex: 'brand_name', render: (v) => v || <Typography.Text type="secondary">any</Typography.Text> },
            { title: 'Category (and descendants)', dataIndex: 'category_taxonomy_path', render: (v) => v || <Typography.Text type="secondary">any</Typography.Text> },
            {
              title: '',
              key: 'actions',
              width: 48,
              render: (_, record) => (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(record.id)}
                  disabled={saving}
                />
              ),
            },
          ]}
          style={{ marginBottom: 12 }}
        />
      )}
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        A new rule's filled-in fields must <strong>all</strong> match (AND) for that rule to grant access; this
        customer's rules combine with <strong>OR</strong> -- any one matching rule is enough.
      </Typography.Paragraph>
      <Space wrap>
        <Input
          placeholder="Manufacturer (optional)"
          value={draft.manufacturer_name}
          onChange={(e) => setDraft((d) => ({ ...d, manufacturer_name: e.target.value }))}
          style={{ width: 180 }}
        />
        <Input
          placeholder="Brand (optional)"
          value={draft.brand_name}
          onChange={(e) => setDraft((d) => ({ ...d, brand_name: e.target.value }))}
          style={{ width: 180 }}
        />
        <Input
          placeholder="Category path, e.g. /hvac/residential (optional)"
          value={draft.category_taxonomy_path}
          onChange={(e) => setDraft((d) => ({ ...d, category_taxonomy_path: e.target.value }))}
          style={{ width: 260 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!hasAnyDimension || saving}
          loading={saving}
          onClick={async () => {
            await onAdd(unitType, draft);
            setDraft(emptyDraft());
          }}
        >
          Add rule
        </Button>
      </Space>
    </Card>
  );
};

const RuleSetAssignmentPanel = ({ unitType, label, allRuleSets, assignments, onAssign, onUnassign, onCreateNew, saving }) => {
  // A multi-slot rule set shows up (and can be assigned/unassigned) in every
  // panel for a slot it applies to -- its single assignment row is what all
  // those panels read from, so toggling it in any one panel keeps the
  // others in sync.
  const options = allRuleSets.filter((rs) => (rs.unit_types || []).includes(unitType));
  const assignedForUnit = assignments.filter((a) => (a.unit_types || []).includes(unitType));
  const selectedIds = assignedForUnit.map((a) => a.rule_set_id);

  const handleChange = async (nextIds) => {
    const added = nextIds.filter((id) => !selectedIds.includes(id));
    const removed = assignedForUnit.filter((a) => !nextIds.includes(a.rule_set_id));
    await Promise.all([
      ...added.map((id) => onAssign(id)),
      ...removed.map((a) => onUnassign(a.assignment_id)),
    ]);
  };

  return (
    <Card
      size="small"
      title={`${label} named rule sets`}
      extra={
        <Button size="small" icon={<PlusOutlined />} onClick={() => onCreateNew(unitType)}>
          New filter
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Assign any number of named rule sets; this customer is eligible if <strong>any</strong> assigned rule set
        matches (combined via OR with each other and with the rules above).
      </Typography.Paragraph>
      {options.length === 0 ? (
        <Empty description={`No ${label.toLowerCase()} rule sets exist yet. Click "New filter" to build one.`} />
      ) : (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select rule sets to assign"
          value={selectedIds}
          disabled={saving}
          onChange={handleChange}
          options={options.map((rs) => {
            const otherSlots = (rs.unit_types || []).filter((s) => s !== unitType);
            return {
              value: rs.id,
              label: otherSlots.length ? `${rs.name} (also: ${otherSlots.join(', ')})` : rs.name,
            };
          })}
        />
      )}
    </Card>
  );
};

const AhriCustomerEligibility = () => {
  const { getAuthHeaders } = useAuth();
  const ruleSetsApi = useMemo(() => makeEligibilityRuleSetsApi(API_BASE_URL, getAuthHeaders), [getAuthHeaders]);
  const [allRuleSets, setAllRuleSets] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsSaving, setAssignmentsSaving] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [rulesByUnitType, setRulesByUnitType] = useState({ indoor: [], outdoor: [] });
  const [rulesLoading, setRulesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ruleSetEditorOpen, setRuleSetEditorOpen] = useState(false);
  const [ruleSetEditorUnitType, setRuleSetEditorUnitType] = useState('indoor');
  const [ruleSetEditorSaving, setRuleSetEditorSaving] = useState(false);

  useEffect(() => {
    const query = customerQuery.trim();
    if (!query) {
      setCustomerOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const results = await listCustomers({ search: query });
        if (!cancelled) setCustomerOptions(Array.isArray(results) ? results : []);
      } catch (err) {
        if (!cancelled) message.error(String(err?.message || err));
      } finally {
        if (!cancelled) setCustomerSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerQuery]);

  const loadRules = useCallback(
    async (customerId) => {
      if (!customerId) return;
      setRulesLoading(true);
      setError('');
      try {
        const [indoorRes, outdoorRes] = await Promise.all(
          UNIT_TYPES.map(({ key }) =>
            fetch(
              `${API_BASE_URL}/api/v1/customers/${encodeURIComponent(customerId)}/unit-eligibility-rules?unit_type=${key}`,
              { headers: getAuthHeaders() }
            ).then(async (r) => {
              const data = await r.json().catch(() => ({}));
              if (!r.ok) throw new Error(String(data?.detail || r.statusText || 'Failed to load rules'));
              return data;
            })
          )
        );
        setRulesByUnitType({
          indoor: indoorRes.rules || [],
          outdoor: outdoorRes.rules || [],
        });
      } catch (err) {
        setError(String(err?.message || err));
      } finally {
        setRulesLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    if (selectedCustomer?.id) loadRules(selectedCustomer.id);
  }, [selectedCustomer, loadRules]);

  const reloadRuleSets = useCallback(async () => {
    try {
      const data = await ruleSetsApi.listRuleSets();
      setAllRuleSets(data.items || []);
    } catch (err) {
      message.error(String(err?.message || err));
    }
  }, [ruleSetsApi]);

  useEffect(() => {
    reloadRuleSets();
  }, [reloadRuleSets]);

  const loadAssignments = useCallback(
    async (customerId) => {
      if (!customerId) return;
      try {
        const data = await ruleSetsApi.listCustomerAssignments(customerId);
        setAssignments(data.assignments || []);
      } catch (err) {
        message.error(String(err?.message || err));
      }
    },
    [ruleSetsApi]
  );

  useEffect(() => {
    if (selectedCustomer?.id) loadAssignments(selectedCustomer.id);
    else setAssignments([]);
  }, [selectedCustomer, loadAssignments]);

  const handleAssignRuleSet = async (ruleSetId) => {
    if (!selectedCustomer?.id) return;
    setAssignmentsSaving(true);
    try {
      await ruleSetsApi.assignRuleSet(selectedCustomer.id, ruleSetId);
      await loadAssignments(selectedCustomer.id);
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setAssignmentsSaving(false);
    }
  };

  const handleUnassignRuleSet = async (assignmentId) => {
    if (!selectedCustomer?.id) return;
    setAssignmentsSaving(true);
    try {
      await ruleSetsApi.unassignRuleSet(selectedCustomer.id, assignmentId);
      await loadAssignments(selectedCustomer.id);
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setAssignmentsSaving(false);
    }
  };

  const openRuleSetEditor = (unitType) => {
    setRuleSetEditorUnitType(unitType);
    setRuleSetEditorOpen(true);
  };

  const handleCreateRuleSet = async (values) => {
    setRuleSetEditorSaving(true);
    try {
      const { rule_set: created } = await ruleSetsApi.createRuleSet(values);
      message.success('Rule set created');
      setRuleSetEditorOpen(false);
      await reloadRuleSets();
      // Immediately assign the newly-built filter to the customer being
      // configured, so building a rule here doesn't require a second trip
      // to re-select it from the dropdown.
      if (selectedCustomer?.id && created?.id) {
        await handleAssignRuleSet(created.id);
      }
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setRuleSetEditorSaving(false);
    }
  };

  const handleAdd = async (unitType, draft) => {
    if (!selectedCustomer?.id) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers/${encodeURIComponent(selectedCustomer.id)}/unit-eligibility-rules`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ unit_type: unitType, ...draft }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to create rule'));
      message.success('Rule added');
      await loadRules(selectedCustomer.id);
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!selectedCustomer?.id) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/customers/${encodeURIComponent(selectedCustomer.id)}/unit-eligibility-rules/${ruleId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data?.detail || response.statusText || 'Failed to delete rule'));
      message.success('Rule removed');
      await loadRules(selectedCustomer.id);
    } catch (err) {
      message.error(String(err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <Typography.Title level={4}>Customer Brand &amp; Category Eligibility</Typography.Title>
      <Typography.Paragraph type="secondary">
        Restrict which manufacturer, brand, and/or category combinations a specific customer can see in the AHRI
        storefront, independently for indoor, outdoor, and furnace units. A customer with no rules is fully
        unrestricted.
      </Typography.Paragraph>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Select
          showSearch
          allowClear
          style={{ width: 420 }}
          placeholder="Search customers by name or email"
          filterOption={false}
          loading={customerSearchLoading}
          onSearch={setCustomerQuery}
          onChange={(value) => {
            const found = customerOptions.find((c) => c.id === value);
            setSelectedCustomer(found || null);
          }}
          value={selectedCustomer?.id}
          options={customerOptions.map((c) => ({
            value: c.id,
            label: c.email ? `${c.name} (${c.email})` : c.name,
          }))}
          notFoundContent={customerSearchLoading ? 'Searching...' : 'Type to search customers'}
        />
      </Card>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {!selectedCustomer ? (
        <Empty description="Select a customer above to manage their eligibility rules." />
      ) : (
        <>
          <Row gutter={16}>
            {UNIT_TYPES.map(({ key, label }) => (
              <Col xs={24} md={12} key={key}>
                <UnitTypePanel
                  unitType={key}
                  label={label}
                  rules={rulesByUnitType[key] || []}
                  onAdd={handleAdd}
                  onDelete={handleDelete}
                  saving={saving || rulesLoading}
                />
              </Col>
            ))}
          </Row>
          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Named rule sets
          </Typography.Title>
          <Row gutter={16}>
            {RULE_SET_SLOTS.map(({ key, label }) => (
              <Col xs={24} md={8} key={key}>
                <RuleSetAssignmentPanel
                  unitType={key}
                  label={label}
                  allRuleSets={allRuleSets}
                  assignments={assignments}
                  onAssign={handleAssignRuleSet}
                  onUnassign={handleUnassignRuleSet}
                  onCreateNew={openRuleSetEditor}
                  saving={assignmentsSaving}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      <EligibilityRuleSetEditor
        open={ruleSetEditorOpen}
        initial={null}
        defaultUnitType={ruleSetEditorUnitType}
        api={ruleSetsApi}
        saving={ruleSetEditorSaving}
        onCancel={() => setRuleSetEditorOpen(false)}
        onSave={handleCreateRuleSet}
      />
    </div>
  );
};

export default AhriCustomerEligibility;
